import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Plus, Calendar, Euro, MapPin, X, RefreshCw, Printer, Edit } from 'lucide-react';
import StampaTesserino from './StampaTesserino';
import { notify } from '../../utils/notifications';

const AbbonamentiCliente = ({ clienteId, abbonamenti, onUpdate, showModal, setShowModal }) => {
  const [linee, setLinee] = useState([]);
  const [tipiAbbonamento, setTipiAbbonamento] = useState([]);
  const [localita, setLocalita] = useState([]);
  // Abbonamento di cui si sta stampando il tesserino
  const [tesserino, setTesserino] = useState(null);
  // Abbonamento in modifica: null quando se ne sta creando uno nuovo
  const [inModifica, setInModifica] = useState(null);
  // All'apertura in modifica i prezzi memorizzati non vanno sovrascritti
  // da quelli di listino: si riallineano solo se si cambia tipo o direzione
  const prezziDaListino = React.useRef(true);
  // Primo anno scolastico non ancora concluso: quello iniziato a settembre
  // di Y finisce il 30 giugno di Y+1, quindi da luglio in poi non va più
  // proposto.
  const annoScolasticoCorrente = (() => {
    const oggi = new Date();
    return oggi.getMonth() + 1 > 6 ? oggi.getFullYear() : oggi.getFullYear() - 1;
  })();

  const [formData, setFormData] = useState({
    linea: '',
    tipo_abbonamento: '',
    direzione: 'ANDATA_RITORNO',
    localita_andata: '',
    localita_ritorno: '',
    anno_scolastico: annoScolasticoCorrente,
    quota_mensile_personalizzata: '',
    importo_settembre: '',
    data_inizio: new Date().toISOString().split('T')[0],
    data_fine: '',
    importo: '',
    note: ''
  });

  const tipoSelezionato = tipiAbbonamento.find(
    (t) => String(t.id) === String(formData.tipo_abbonamento)
  );
  const isScuole = tipoSelezionato?.modalita === 'SCUOLE';

  // Prezzo di listino del tipo: punto di partenza sovrascrivibile
  const quotaDaListino = tipoSelezionato
    ? (formData.direzione === 'ANDATA_RITORNO'
        ? Number(tipoSelezionato.importo)
        : Number(tipoSelezionato.importo_sola_direzione ?? tipoSelezionato.importo))
    : 0;

  useEffect(() => {
    if (!isScuole) return;
    if (!prezziDaListino.current) {
      prezziDaListino.current = true;
      return;
    }
    setFormData((f) => ({
      ...f,
      quota_mensile_personalizzata: quotaDaListino.toFixed(2),
      importo_settembre: (
        tipoSelezionato.importo_settembre !== null && tipoSelezionato.importo_settembre !== undefined
          ? Number(tipoSelezionato.importo_settembre)
          : quotaDaListino / 2
      ).toFixed(2),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.tipo_abbonamento, formData.direzione]);

  const quotaMensile = Number(formData.quota_mensile_personalizzata) || 0;
  const quotaSettembre = Number(formData.importo_settembre) || 0;
  const totaleAnnuo = (quotaSettembre + quotaMensile * 8).toFixed(2);

  useEffect(() => {
    caricaLinee();
  }, []);

  const caricaLinee = async () => {
    try {
      const res = await api.get('/trasporti/linee/');
      setLinee(res.data.results || res.data);
    } catch (error) {
      console.error('Errore caricamento linee:', error);
    }
  };

  const handleLineaChange = async (lineaId) => {
    setFormData({ ...formData, linea: lineaId, tipo_abbonamento: '', importo: '', data_fine: '' });
    
    if (lineaId) {
      try {
        const res = await api.get(`/abbonamenti/tipi-abbonamento/?linea=${lineaId}&attivo=true`);
        setTipiAbbonamento(res.data.results || res.data);
        const locRes = await api.get(`/trasporti/localita/?gruppo=${lineaId}&attiva=true`);
        setLocalita(locRes.data.results || locRes.data);
      } catch (error) {
        console.error('Errore caricamento tipi abbonamento:', error);
        const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Errore nel caricamento dei tipi di abbonamento per questa linea';
        notify.error(errorMsg);
      }
    } else {
      setTipiAbbonamento([]);
      setLocalita([]);
    }
  };

  const handleTipoChange = (tipoId) => {
    const tipo = tipiAbbonamento.find(t => t.id === parseInt(tipoId));
    if (!tipo) return;

    // Negli abbonamenti scolastici le date le calcola il server
    // dall'anno scolastico: qui non c'è nulla da dedurre.
    if (tipo.modalita === 'SCUOLE') {
      setFormData({
        ...formData,
        tipo_abbonamento: tipoId,
        importo: '',
        data_fine: '',
      });
      return;
    }

    const dataInizio = new Date(formData.data_inizio);
    const dataFine = new Date(dataInizio);
    dataFine.setDate(dataFine.getDate() + (tipo.durata_giorni || 0));

    setFormData({
      ...formData,
      tipo_abbonamento: tipoId,
      importo: tipo.importo,
      data_fine: dataFine.toISOString().split('T')[0]
    });
  };

  const apriModifica = async (abb) => {
    prezziDaListino.current = false;   // conserva i prezzi già memorizzati
    setInModifica(abb);
    // Tipi e località appartengono al gruppo: vanno caricati prima
    if (abb.linea) {
      try {
        const [tipiRes, locRes] = await Promise.all([
          api.get(`/abbonamenti/tipi-abbonamento/?linea=${abb.linea}&attivo=true`),
          api.get(`/trasporti/localita/?gruppo=${abb.linea}&attiva=true`),
        ]);
        setTipiAbbonamento(tipiRes.data.results || tipiRes.data);
        setLocalita(locRes.data.results || locRes.data);
      } catch (error) {
        console.error('Errore caricamento dati del gruppo:', error);
      }
    }
    setFormData({
      linea: abb.linea || '',
      tipo_abbonamento: abb.tipo_abbonamento || '',
      direzione: abb.direzione || 'ANDATA_RITORNO',
      localita_andata: abb.localita_andata || '',
      localita_ritorno: abb.localita_ritorno || '',
      anno_scolastico: abb.anno_scolastico || annoScolasticoCorrente,
      quota_mensile_personalizzata: abb.quota_mensile_applicata ?? '',
      importo_settembre: abb.quota_settembre_applicata ?? '',
      data_inizio: abb.data_inizio || '',
      data_fine: abb.data_fine || '',
      importo: abb.importo || '',
      note: abb.note || '',
    });
    setShowModal(true);
  };

  const chiudiModale = () => {
    setShowModal(false);
    setInModifica(null);
    prezziDaListino.current = true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const toastId = notify.loading(inModifica ? 'Aggiornamento abbonamento...' : 'Creazione abbonamento...');

    const payload = {
      titolare: clienteId,
      linea: formData.linea,
      tipo_abbonamento: formData.tipo_abbonamento,
      direzione: formData.direzione,
      localita_andata: formData.localita_andata || null,
      localita_ritorno: formData.localita_ritorno || null,
    };
    if (!inModifica) {
      payload.stato = 'IN_ATTESA_PAGAMENTO';
    }
    if (isScuole) {
      payload.anno_scolastico = formData.anno_scolastico;
      payload.quota_mensile_personalizzata = formData.quota_mensile_personalizzata;
      payload.importo_settembre = formData.importo_settembre;
    } else {
      payload.data_inizio = formData.data_inizio;
      payload.data_fine = formData.data_fine;
      payload.importo = formData.importo;
    }

    try {
      if (inModifica) {
        await api.patch(`/abbonamenti/abbonamenti/${inModifica.id}/`, payload);
        notify.update(toastId, {
          type: 'success',
          render: 'Abbonamento aggiornato. Le mensilità non ancora incassate sono state ricalcolate.',
        });
      } else {
        await api.post('/abbonamenti/abbonamenti/', payload);
        notify.update(toastId, { type: 'success', render: 'Abbonamento creato!' });
      }
      chiudiModale();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Errore creazione abbonamento:', error);
      const dati = error.response?.data || {};
      const primo = Object.values(dati).flat().find((v) => typeof v === 'string');
      notify.update(toastId, {
        type: 'error',
        render: dati.detail || primo || error.message || 'Errore nel salvataggio dell\'abbonamento',
      });
    }
  };

  const handleRinnova = async (abbonamentoId, isScuole) => {
    const conferma = isScuole
      ? "Rinnovare l'abbonamento per il prossimo anno scolastico? Verranno generate le nuove mensilità; quelle dell'anno concluso restano in archivio."
      : 'Confermi di voler rinnovare questo abbonamento? Il periodo verrà esteso.';
    if (!window.confirm(conferma)) return;

    const toastId = notify.loading('Rinnovo abbonamento...');

    try {
      const res = await api.post(`/abbonamenti/abbonamenti/${abbonamentoId}/rinnova/`);
      notify.update(toastId, {
        type: 'success',
        render: res.data?.message || 'Abbonamento rinnovato! Registra il pagamento per attivarlo.',
      });
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Errore rinnovo abbonamento:', error);
      const dati = error.response?.data || {};
      const errorMsg = dati.errore || dati.detail || dati.message || error.message || 'Errore nel rinnovo dell\'abbonamento';
      notify.update(toastId, { type: 'error', render: errorMsg });
    }
  };

  const getStatoBadge = (abbonamento) => {
    // Usa badge_info se disponibile dal backend
    if (abbonamento.badge_info) {
      const colori = {
        'red': 'bg-red-100 text-red-800 border-red-300',
        'orange': 'bg-orange-100 text-orange-800 border-orange-300',
        'yellow': 'bg-yellow-100 text-yellow-800 border-yellow-300',
        'green': 'bg-green-100 text-green-800 border-green-300',
        'blue': 'bg-blue-100 text-blue-800 border-blue-300',
        'gray': 'bg-gray-100 text-gray-800 border-gray-300'
      };
      
      return {
        classe: colori[abbonamento.badge_info.colore] || 'bg-gray-100 text-gray-800',
        icona: abbonamento.badge_info.icona,
        stato: abbonamento.badge_info.stato,
        messaggio: abbonamento.badge_info.messaggio
      };
    }
    
    // Fallback per compatibilità
    const stato = abbonamento.stato_effettivo || abbonamento.stato;
    const badges = {
      'ATTIVO': { classe: 'bg-green-100 text-green-800', icona: '✅', stato: 'ATTIVO' },
      'SCADUTO': { classe: 'bg-red-100 text-red-800', icona: '❌', stato: 'SCADUTO' },
      'IN_ATTESA_PAGAMENTO': { classe: 'bg-orange-100 text-orange-800', icona: '⏳', stato: 'IN ATTESA PAGAMENTO' },
      'SOSPESO': { classe: 'bg-gray-100 text-gray-800', icona: '⏸️', stato: 'SOSPESO' },
      'REVOCATO': { classe: 'bg-red-100 text-red-800', icona: '⛔', stato: 'REVOCATO' }
    };
    return badges[stato] || { classe: 'bg-gray-100 text-gray-800', icona: '❓', stato: stato };
  };

  return (
    <div>
      {abbonamenti.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">Nessun abbonamento presente</p>
      ) : (
        <div className="space-y-3">
          {abbonamenti.map((abb) => {
            const badge = getStatoBadge(abb);
            return (
            <div key={abb.id} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <p className="font-bold text-gray-900">{abb.codice}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" /> {abb.tratta || abb.linea_nome}
                  </p>
                  {abb.tipo_abbonamento_nome && (
                    <p className="text-sm text-gray-600">{abb.tipo_abbonamento_nome}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <span className={`px-3 py-1 text-xs rounded-full border ${badge.classe} font-semibold flex items-center gap-1`}>
                      <span>{badge.icona}</span>
                      <span>{badge.stato}</span>
                    </span>
                    {badge.messaggio && (
                      <p className="text-xs text-gray-500 mt-1">{badge.messaggio}</p>
                    )}
                  </div>
                  <button
                    onClick={() => apriModifica(abb)}
                    className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition"
                    title="Modifica gruppo, tipo e tratta"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setTesserino(abb)}
                    className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                    title="Stampa tesserino"
                  >
                    <Printer className="w-4 h-4" />
                  </button>
                  {/* Gli abbonamenti scolastici si rinnovano solo a anno
                      concluso: il backend espone puo_essere_rinnovato */}
                  {abb.puo_essere_rinnovato && (
                    <button
                      onClick={() => handleRinnova(abb.id, abb.modalita === 'SCUOLE')}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      title={
                        abb.modalita === 'SCUOLE'
                          ? 'Rinnova per il prossimo anno scolastico'
                          : 'Rinnova abbonamento'
                      }
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  )}
                  {!abb.puo_essere_rinnovato && abb.motivo_blocco_rinnovo && abb.modalita === 'SCUOLE' && (
                    <span
                      title={abb.motivo_blocco_rinnovo}
                      className="p-2 text-gray-300 cursor-help"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-200">
                <div className="text-sm">
                  <p className="text-gray-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Periodo
                  </p>
                  <p className="font-semibold text-gray-900">
                    {new Date(abb.data_inizio).toLocaleDateString('it-IT')} - {new Date(abb.data_fine).toLocaleDateString('it-IT')}
                  </p>
                </div>
                <div className="text-sm">
                  <p className="text-gray-500 flex items-center gap-1">
                    <Euro className="w-3 h-3" /> Importo
                  </p>
                  <p className="font-semibold text-green-600">€{abb.importo}</p>
                </div>
              </div>
              
              {/* Barra di progresso utilizzo */}
              {abb.percentuale_utilizzo !== undefined && (abb.stato_effettivo === 'ATTIVO' || abb.stato === 'ATTIVO') && (
                <div className="mt-3">
                  <div className="flex flex-wrap justify-between items-center gap-3 mb-1">
                    <p className="text-xs text-gray-500">Utilizzo</p>
                    <p className="text-xs font-semibold text-gray-700">{abb.percentuale_utilizzo}%</p>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        abb.percentuale_utilizzo > 80 ? 'bg-red-500' :
                        abb.percentuale_utilizzo > 60 ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`}
                      style={{ width: `${abb.percentuale_utilizzo}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          )})}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full">
            <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">
                {inModifica ? `Modifica ${inModifica.codice}` : 'Nuovo Abbonamento'}
              </h2>
              <button onClick={chiudiModale} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {inModifica && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-900">
                    Cambiando gruppo o tipo si ricalcolano le mensilità <strong>non ancora
                    incassate</strong>. Quelle già saldate restano al valore con cui sono
                    state pagate.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Linea *</label>
                <select
                  required
                  value={formData.linea}
                  onChange={(e) => handleLineaChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                >
                  <option value="">Seleziona linea...</option>
                  {linee.map((linea) => (
                    <option key={linea.id} value={linea.id}>{linea.nome}</option>
                  ))}
                </select>
              </div>

              {formData.linea && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Tipo Abbonamento *</label>
                  <select
                    required
                    value={formData.tipo_abbonamento}
                    onChange={(e) => handleTipoChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                  >
                    <option value="">Seleziona tipo...</option>
                    {tipiAbbonamento.map((tipo) => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.nome} - {tipo.modalita === 'SCUOLE'
                          ? `€${tipo.importo}/mese`
                          : `€${tipo.importo} (${tipo.durata_giorni} giorni)`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Direzione *</label>
                <select
                  required
                  value={formData.direzione}
                  onChange={(e) => setFormData({ ...formData, direzione: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                >
                  <option value="ANDATA_RITORNO">Andata e ritorno</option>
                  <option value="SOLO_ANDATA">Solo andata</option>
                  <option value="SOLO_RITORNO">Solo ritorno</option>
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Località salita (andata)</label>
                  <select
                    disabled={!formData.linea || formData.direzione === 'SOLO_RITORNO'}
                    value={formData.localita_andata}
                    onChange={(e) => setFormData({ ...formData, localita_andata: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce] disabled:bg-gray-100"
                  >
                    <option value="">Nessuna</option>
                    {localita.map((l) => (
                      <option key={l.id} value={l.id}>{l.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Località discesa (ritorno)</label>
                  <select
                    disabled={!formData.linea || formData.direzione === 'SOLO_ANDATA'}
                    value={formData.localita_ritorno}
                    onChange={(e) => setFormData({ ...formData, localita_ritorno: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce] disabled:bg-gray-100"
                  >
                    <option value="">Nessuna</option>
                    {localita.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.nome}{l.orario_ritorno ? ` — rientro ${l.orario_ritorno.slice(0,5)}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {isScuole ? (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Anno scolastico *</label>
                  <select
                    required
                    value={formData.anno_scolastico}
                    onChange={(e) => setFormData({ ...formData, anno_scolastico: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                  >
                    {[annoScolasticoCorrente, annoScolasticoCorrente + 1].map((anno) => (
                      <option key={anno} value={anno}>{anno}/{anno + 1}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1">
                    Dal 15 settembre {formData.anno_scolastico} al 30 giugno {Number(formData.anno_scolastico) + 1}.
                    Giugno è gratuito.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Quota mensile (€) *</label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={formData.quota_mensile_personalizzata}
                        onChange={(e) => setFormData({ ...formData, quota_mensile_personalizzata: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">Listino: €{quotaDaListino.toFixed(2)}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Quota settembre (€) *</label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={formData.importo_settembre}
                        onChange={(e) => setFormData({ ...formData, importo_settembre: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">Mezzo mese (15-30 settembre)</p>
                    </div>
                  </div>

                  <p className="text-xs text-gray-900 mt-3 pt-2 border-t border-gray-200">
                    Totale anno: <strong>€{totaleAnnuo}</strong>
                  </p>
                </div>
              ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Data Inizio *</label>
                  <input
                    type="date"
                    required
                    value={formData.data_inizio}
                    onChange={(e) => {
                      const nuovaDataInizio = e.target.value;
                      const tipo = tipiAbbonamento.find(t => t.id === parseInt(formData.tipo_abbonamento));
                      if (tipo && nuovaDataInizio) {
                        const dataFine = new Date(nuovaDataInizio);
                        dataFine.setDate(dataFine.getDate() + tipo.durata_giorni);
                        setFormData({ ...formData, data_inizio: nuovaDataInizio, data_fine: dataFine.toISOString().split('T')[0] });
                      } else {
                        setFormData({ ...formData, data_inizio: nuovaDataInizio });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Data Fine</label>
                  <input
                    type="date"
                    value={formData.data_fine}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                  />
                </div>
              </div>
              )}

              {!isScuole && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Importo</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.importo}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Note</label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={chiudiModale}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={!formData.tipo_abbonamento}
                  className="px-6 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition font-semibold disabled:opacity-50"
                >
                  Crea Abbonamento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {tesserino && (
        <StampaTesserino
          abbonamento={tesserino}
          onClose={() => setTesserino(null)}
        />
      )}
    </div>
  );
};

export default AbbonamentiCliente;
